// Sportief dossier van één persoon (fase 1, stap 2): uitslagen met
// bewijsstukken, en graduaties uit het register en uit examens.
//
// Gedeeld door het ledenbestand (club) en de eigen omgeving (lid of ouder).
// Wat iemand mag, zegt de database in dossier().rechten; de regels zelf
// (bevestigen, vastliggen na bevestiging, pad van een bestand) bewaken de
// triggers en policies. Dit scherm legt voor en toont.
//
//   await toonDossier(el, sb, persoonId)
import { t, locale } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from './toast.js';
import { showConfirm } from './confirm.js';
import { vraagReden } from './reden.js';

export const ONDERDELEN = ['tul', 'sparring', 'speciale_techniek', 'breektest',
  'team_tul', 'team_sparring', 'team_speciale_techniek', 'team_breektest', 'overig'];
export const NIVEAUS = ['club', 'regionaal', 'nationaal', 'internationaal'];
export const BESTANDSTYPEN = { 'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/png': 'png' };
export const MAX_BESTAND = 10 * 1024 * 1024;
const BUCKET = 'dossier';
const STATUSKLEUR = { bevestigd: 'badge-green', onbevestigd: 'badge-amber', afgewezen: 'badge-red' };

function datum(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
const klein = tekst => `<p style="font-size:12px;color:var(--text-3);margin:4px 0">${esc(tekst)}</p>`;
const knop = (actie, icoon, tekst, soort = 'btn-default', extra = '') =>
  `<button type="button" class="btn ${soort} btn-sm" data-dossier="${actie}" ${extra}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;

export function dossierFout(e) {
  const m = String(e?.message || e || '').match(/dossier:([a-z_]+)/);
  if (m) return t('dossier.fout.' + m[1]);
  if (/row-level security|violates row/i.test(String(e?.message || ''))) return t('dossier.fout.geen_recht');
  return t('alg.fout', { melding: e?.message || String(e) });
}

// Controle vooraf, zodat een lid niet eerst 10 MB uploadt om dan te horen
// dat het niet mag. De bucket en de tabel controleren hetzelfde.
export function bestandProbleem(bestand) {
  if (!bestand) return null;
  if (!BESTANDSTYPEN[bestand.type]) return 'dossier.fout.bestandstype';
  if (bestand.size > MAX_BESTAND) return 'dossier.fout.bestand_te_groot';
  return null;
}

// Uniek pad per bestand: <organisatie>/<persoon>/<willekeurig>.<ext>. De
// oorspronkelijke naam staat alleen in de tabel, niet in het pad.
export function bestandPad(org, persoon, mime) {
  const id = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${org}/${persoon}/${id}.${BESTANDSTYPEN[mime]}`;
}

export function klasseringTekst(k) {
  if (k == null || k === '') return t('dossier.klassering.geen');
  return t('dossier.klassering.plaats', { plaats: k });
}

// Stand per container, op het element zelf: verdwijnt met het element.
const staat = {
  get: el => el.__dossier,
  set: (el, s) => { el.__dossier = s; },
};

export async function toonDossier(el, sb, persoonId) {
  if (!el) return;
  const bestaand = staat.get(el);
  const s = bestaand && bestaand.persoon === persoonId ? bestaand : { sb, persoon: persoonId, data: null, bewerk: null, nieuw: false };
  s.sb = sb;
  staat.set(el, s);
  if (!el.dataset.dossierGekoppeld) {
    el.dataset.dossierGekoppeld = '1';
    el.addEventListener('click', e => klik(el, e));
    el.addEventListener('submit', e => e.preventDefault());
    koppelBestandkeuze(el);
  }
  el.innerHTML = '<div class="spinner"></div>';
  const { data, error } = await sb.rpc('dossier', { p_persoon: persoonId });
  if (error || !data) {
    el.innerHTML = `<div class="section-title" style="margin-top:8px"><i class="ti ti-trophy"></i> ${esc(t('dossier.titel'))}</div>${klein(dossierFout(error))}`;
    return;
  }
  s.data = data;
  teken(el);
}

function teken(el) {
  const s = staat.get(el);
  const d = s.data;
  const r = d.rechten || {};
  const uitslagen = d.uitslagen || [];
  const open = uitslagen.filter(u => u.status === 'onbevestigd').length;
  const graduaties = (d.graduaties || []).map(g => `<div class="org-row" style="padding:6px 0">
      <div style="flex:1;font-size:13px">${esc(g.naam || g.code || '')}</div>
      <span style="font-size:12px;color:var(--text-3)">${esc([datum(g.datum), t('dossier.graad.bron.' + g.bron)].filter(Boolean).join(' · '))}</span></div>`).join('');
  el.innerHTML = `
    <div class="section-title" style="margin-top:8px"><i class="ti ti-trophy"></i> ${esc(t('dossier.titel'))}</div>
    ${klein(t(r.club ? 'dossier.uitleg.club' : 'dossier.uitleg.lid'))}
    <div style="font-size:12px;font-weight:600;margin:10px 0 4px">${esc(t('dossier.graduaties'))}</div>
    ${graduaties || klein(d.persoon?.graad_opgave ? t('dossier.graad.opgave', { graad: d.persoon.graad_opgave }) : t('dossier.graduaties.leeg'))}
    <div style="display:flex;align-items:center;gap:8px;margin:12px 0 4px;flex-wrap:wrap">
      <span style="font-size:12px;font-weight:600">${esc(t('dossier.uitslagen'))}</span>
      ${open && r.club ? `<span class="badge badge-amber">${esc(t('dossier.open', { aantal: open }))}</span>` : ''}
      <span style="margin-left:auto"></span>
      ${r.schrijven && !s.nieuw && !s.bewerk ? knop('nieuw', 'plus', t('dossier.nieuw')) : ''}
    </div>
    ${s.nieuw ? formulier(null) : ''}
    ${uitslagen.map(u => s.bewerk === u.id ? formulier(u) : rij(u, r)).join('') || (s.nieuw ? '' : klein(t('dossier.uitslagen.leeg')))}`;
}

function rij(u, r) {
  const magLid = r.schrijven && u.status !== 'bevestigd';
  const magWijzigen = r.club || magLid;
  const plaats = [u.stad, u.land].filter(Boolean).join(', ');
  const bewijs = (u.bewijs || []).map(b => `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:8px">
      <a href="#" data-dossier="open" data-pad="${esc(b.storage_path)}" style="font-size:12px"><i class="ti ti-${b.mime === 'application/pdf' ? 'file-type-pdf' : 'photo'}"></i> ${esc(b.bestandsnaam)}</a>
      ${magWijzigen ? `<button type="button" class="btn-icon" data-dossier="bewijsweg" data-id="${esc(b.id)}" data-pad="${esc(b.storage_path)}" title="${esc(t('dossier.bewijs.weg'))}"><i class="ti ti-x"></i></button>` : ''}</span>`).join('');
  return `<div class="org-row" data-uitslag="${esc(u.id)}" style="flex-wrap:wrap;gap:8px;align-items:flex-start">
    <div style="flex:1;min-width:220px">
      <div style="font-size:13px"><strong>${esc(u.toernooi)}</strong>
        <span class="badge ${STATUSKLEUR[u.status] || 'badge-grey'}">${esc(t('dossier.status.' + u.status))}</span></div>
      <div style="font-size:12px;color:var(--text-2)">${esc([datum(u.datum), plaats, u.niveau ? t('dossier.niveau.' + u.niveau) : ''].filter(Boolean).join(' · '))}</div>
      <div style="font-size:12px">${esc([t('dossier.onderdeel.' + u.onderdeel), u.categorie, klasseringTekst(u.klassering)].filter(Boolean).join(' · '))}</div>
      ${u.toelichting ? `<div style="font-size:12px;color:var(--text-3)">${esc(u.toelichting)}</div>` : ''}
      ${u.status === 'afgewezen' && u.afwijsreden ? `<div style="font-size:12px;color:var(--red)">${esc(t('dossier.afgewezenom', { reden: u.afwijsreden }))}</div>` : ''}
      <div style="font-size:11px;color:var(--text-3)">${esc(t('dossier.bron.' + u.bron))}</div>
      ${bewijs ? `<div style="margin-top:4px">${bewijs}</div>` : ''}
    </div>
    <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
      ${r.club && u.status === 'onbevestigd' ? knop('bevestig', 'check', t('dossier.bevestig'), 'btn-primary', `data-id="${esc(u.id)}"`)
        + knop('afwijs', 'x', t('dossier.afwijs'), 'btn-default', `data-id="${esc(u.id)}"`) : ''}
      ${magWijzigen && (u.bewijs || []).length < 5 ? `<label class="btn btn-ghost btn-sm" style="cursor:pointer"><i class="ti ti-paperclip"></i> ${esc(t('dossier.bewijs.toevoegen'))}
        <input type="file" accept="application/pdf,image/jpeg,image/png" data-dossier-bestand="${esc(u.id)}" hidden></label>` : ''}
      ${magWijzigen ? knop('wijzig', 'pencil', t('dossier.wijzig'), 'btn-ghost', `data-id="${esc(u.id)}"`) : ''}
      ${magWijzigen ? knop('weg', 'trash', t('alg.verwijderen'), 'btn-ghost', `data-id="${esc(u.id)}"`) : ''}
    </div>
  </div>`;
}

function formulier(u) {
  const v = u || { datum: '', onderdeel: 'tul' };
  const optie = (w, tekst, gekozen) => `<option value="${esc(w)}"${gekozen ? ' selected' : ''}>${esc(tekst)}</option>`;
  const veld = (naam, label, type = 'text', extra = '') => `<div class="form-group"><label class="form-label">${esc(label)}</label>
    <input class="form-input" type="${type}" name="${naam}" value="${esc(v[naam] == null ? '' : String(v[naam]))}" ${extra}></div>`;
  const vandaag = new Date().toISOString().slice(0, 10);
  return `<form class="settings-card" data-dossierformulier="${esc(u?.id || '')}" style="margin:8px 0">
    <div class="form-row">${veld('datum', t('dossier.veld.datum'), 'date', `max="${vandaag}" required`)}${veld('toernooi', t('dossier.veld.toernooi'), 'text', `maxlength="200" placeholder="${esc(t('dossier.veld.toernooi.voorbeeld'))}"`)}</div>
    <div class="form-row">${veld('stad', t('dossier.veld.stad'), 'text', 'maxlength="100"')}${veld('land', t('dossier.veld.land'), 'text', 'maxlength="2" placeholder="NL" style="text-transform:uppercase"')}
      <div class="form-group"><label class="form-label">${esc(t('dossier.veld.niveau'))}</label>
        <select class="form-input" name="niveau">${optie('', '', !v.niveau)}${NIVEAUS.map(n => optie(n, t('dossier.niveau.' + n), v.niveau === n)).join('')}</select></div></div>
    <div class="form-row">
      <div class="form-group"><label class="form-label">${esc(t('dossier.veld.onderdeel'))}</label>
        <select class="form-input" name="onderdeel">${ONDERDELEN.map(o => optie(o, t('dossier.onderdeel.' + o), v.onderdeel === o)).join('')}</select></div>
      ${veld('categorie', t('dossier.veld.categorie'), 'text', `maxlength="120" placeholder="${esc(t('dossier.veld.categorie.voorbeeld'))}"`)}
      ${veld('klassering', t('dossier.veld.klassering'), 'number', 'min="1" max="999" step="1"')}</div>
    ${veld('toelichting', t('dossier.veld.toelichting'), 'text', 'maxlength="500"')}
    ${u ? '' : `<div class="form-group"><label class="form-label">${esc(t('dossier.veld.bewijs'))}</label>
      <input class="form-input" type="file" name="bewijs" accept="application/pdf,image/jpeg,image/png">
      ${klein(t('dossier.bewijs.uitleg'))}</div>`}
    <div style="display:flex;gap:8px;justify-content:flex-end">
      ${knop('annuleer', 'x', t('alg.annuleren'), 'btn-ghost')}
      ${knop('opslaan', 'device-floppy', t('alg.opslaan'), 'btn-primary', u ? `data-id="${esc(u.id)}"` : '')}
    </div>
  </form>`;
}

function waarden(form) {
  const w = n => form.querySelector(`[name="${n}"]`)?.value.trim() ?? '';
  const k = w('klassering');
  return {
    datum: w('datum') || null,
    toernooi: w('toernooi'),
    stad: w('stad') || null,
    land: w('land').toUpperCase() || null,
    niveau: w('niveau') || null,
    onderdeel: w('onderdeel'),
    categorie: w('categorie') || null,
    klassering: k === '' ? null : Number(k),
    toelichting: w('toelichting') || null,
  };
}

async function voegBewijsToe(s, uitslagId, bestand) {
  const probleem = bestandProbleem(bestand);
  if (probleem) { toast(t(probleem), 'error'); return false; }
  const p = s.data.persoon;
  const pad = bestandPad(p.organisation_id, p.id, bestand.type);
  const opslag = s.sb.storage.from(BUCKET);
  const up = await opslag.upload(pad, bestand, { contentType: bestand.type, upsert: false });
  if (up?.error) { toast(dossierFout(up.error), 'error'); return false; }
  const { error } = await s.sb.from('sport_evidence').insert({
    result_id: uitslagId, organisation_id: p.organisation_id, person_id: p.id,
    storage_path: pad, bestandsnaam: bestand.name || 'bestand', mime: bestand.type, grootte: bestand.size,
  });
  if (error) {
    await opslag.remove([pad]);   // geen wees achterlaten
    toast(dossierFout(error), 'error');
    return false;
  }
  return true;
}

async function opslaan(el, id) {
  const s = staat.get(el);
  const form = el.querySelector(`[data-dossierformulier="${id || ''}"]`);
  const rij = waarden(form);
  if (!rij.datum || !rij.toernooi) { toast(t('dossier.fout.verplicht'), 'error'); return; }
  if (rij.land && !/^[A-Z]{2}$/.test(rij.land)) { toast(t('dossier.fout.land'), 'error'); return; }
  const bestand = form.querySelector('[name="bewijs"]')?.files?.[0] || null;
  const probleem = bestandProbleem(bestand);
  if (probleem) { toast(t(probleem), 'error'); return; }
  const p = s.data.persoon;
  let res;
  if (id) res = await s.sb.from('sport_results').update(rij).eq('id', id).select('id, status').maybeSingle();
  else res = await s.sb.from('sport_results').insert({ ...rij, person_id: p.id, organisation_id: p.organisation_id }).select('id, status').maybeSingle();
  if (res.error) { toast(dossierFout(res.error), 'error'); return; }
  if (bestand && res.data?.id) await voegBewijsToe(s, res.data.id, bestand);
  toast(t(res.data?.status === 'onbevestigd' ? 'dossier.opgeslagen.open' : 'dossier.opgeslagen'), 'success');
  s.nieuw = false; s.bewerk = null;
  await toonDossier(el, s.sb, s.persoon);
}

async function besluit(el, id, akkoord) {
  const s = staat.get(el);
  let reden = null;
  if (!akkoord) {
    const r = await vraagReden({ titel: t('dossier.afwijs'), uitleg: t('dossier.afwijs.uitleg'), knop: t('dossier.afwijs'), voorbeeld: t('dossier.afwijs.voorbeeld') });
    if (!r) return;
    reden = r.reden;
  }
  const { error } = await s.sb.rpc('uitslag_besluiten', { p_uitslag: id, p_akkoord: akkoord, p_reden: reden });
  if (error) { toast(dossierFout(error), 'error'); return; }
  toast(t(akkoord ? 'dossier.bevestigd' : 'dossier.afgewezen'), 'success');
  await toonDossier(el, s.sb, s.persoon);
}

async function openBestand(s, pad) {
  // Kort geldige link: wie hem doorstuurt, geeft na een minuut niets meer weg.
  const { data, error } = await s.sb.storage.from(BUCKET).createSignedUrl(pad, 60);
  if (error || !data?.signedUrl) { toast(dossierFout(error), 'error'); return; }
  window.open(data.signedUrl, '_blank', 'noopener');
}

async function klik(el, e) {
  const s = staat.get(el);
  const k = e.target.closest('[data-dossier]');
  if (!k || !s) return;
  e.preventDefault();
  const id = k.dataset.id;
  switch (k.dataset.dossier) {
    case 'nieuw': s.nieuw = true; s.bewerk = null; teken(el); break;
    case 'wijzig': s.bewerk = id; s.nieuw = false; teken(el); break;
    case 'annuleer': s.nieuw = false; s.bewerk = null; teken(el); break;
    case 'opslaan': await opslaan(el, id || null); break;
    case 'bevestig': await besluit(el, id, true); break;
    case 'afwijs': await besluit(el, id, false); break;
    case 'open': await openBestand(s, k.dataset.pad); break;
    case 'bewijsweg': {
      const pad = k.dataset.pad;
      showConfirm(t('dossier.bewijs.weg'), t('dossier.bewijs.weg.vraag'), async () => {
        const { error } = await s.sb.from('sport_evidence').delete().eq('id', id);
        if (error) { toast(dossierFout(error), 'error'); return; }
        await s.sb.storage.from(BUCKET).remove([pad]);
        await toonDossier(el, s.sb, s.persoon);
      });
      break;
    }
    case 'weg': {
      const u = (s.data.uitslagen || []).find(x => x.id === id);
      showConfirm(t('alg.verwijderen'), t('dossier.weg.vraag', { toernooi: u?.toernooi || '' }), async () => {
        const paden = (u?.bewijs || []).map(b => b.storage_path);
        const { error } = await s.sb.from('sport_results').delete().eq('id', id);
        if (error) { toast(dossierFout(error), 'error'); return; }
        if (paden.length) await s.sb.storage.from(BUCKET).remove(paden);
        toast(t('dossier.verwijderd'), 'success');
        await toonDossier(el, s.sb, s.persoon);
      });
      break;
    }
    default: break;
  }
}

// Bestand kiezen bij een bestaande uitslag: meteen uploaden.
function koppelBestandkeuze(el) {
  if (el.dataset.dossierBestandGekoppeld) return;
  el.dataset.dossierBestandGekoppeld = '1';
  el.addEventListener('change', async e => {
    const invoer = e.target.closest('[data-dossier-bestand]');
    const s = staat.get(el);
    if (!invoer || !s || !invoer.files?.[0]) return;
    if (await voegBewijsToe(s, invoer.dataset.dossierBestand, invoer.files[0])) {
      toast(t('dossier.bewijs.toegevoegd'), 'success');
      await toonDossier(el, s.sb, s.persoon);
    }
  });
}
